import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { financesService } from "@/features/finances/services/financesService"

// Rent Records
export function useRentRecords(lease_id?: string) {
  return useQuery({
    queryKey: lease_id ? ["rent-records", { lease_id }] : ["rent-records"],
    queryFn: () => financesService.listRentRecords(lease_id),
  })
}

export function useRentRecord(id: string) {
  return useQuery({
    queryKey: ["rent-records", id],
    queryFn: () => financesService.getRentRecord(id),
    enabled: !!id,
  })
}

export function useCreateRentRecord() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      financesService.createRentRecord(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rent-records"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useUpdateRentRecord() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      financesService.updateRentRecord(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rent-records"] })
      queryClient.invalidateQueries({ queryKey: ["rent-records", id] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useDeleteRentRecord() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => financesService.deleteRentRecord(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rent-records"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

// Expenses
export function useExpenses(property_id?: string) {
  return useQuery({
    queryKey: property_id ? ["expenses", { property_id }] : ["expenses"],
    queryFn: () => financesService.listExpenses(property_id),
  })
}

export function useCreateExpense() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      financesService.createExpense(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useUpdateExpense() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      financesService.updateExpense(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] })
      queryClient.invalidateQueries({ queryKey: ["expenses", id] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useDeleteExpense() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => financesService.deleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

// Manual Earnings
export function useManualEarnings() {
  return useQuery({
    queryKey: ["manual-earnings"],
    queryFn: () => financesService.listManualEarnings(),
  })
}

export function useCreateManualEarning() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => financesService.createManualEarning(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["manual-earnings"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useUpdateManualEarning() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      financesService.updateManualEarning(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["manual-earnings"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

export function useDeleteManualEarning() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => financesService.deleteManualEarning(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["manual-earnings"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] })
    },
  })
}

// Dashboard Stats
export function useDashboardStats() {
  return useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => financesService.getDashboardStats(),
  })
}
